import { emitProgressionChanged } from './parcoursEvents';
import { getStorageKey } from './userStorage';

// Structure de données initiale pour un nouvel utilisateur
const initialProgress = {
  userProgress: {
    lastPlayedExercise: "",
    currentHighestNode: "node-1",
    nodes: {
      "node-1": {
        completed: false,
        currentExercise: "",
        exercises: [],
        progress: {}
      },
      "node-2": {
        completed: false,
        currentExercise: "",
        exercises: [],
        progress: {}
      },
      "node-3": {
        completed: false,
        currentExercise: "",
        exercises: [],
        progress: {}
      },
      "node-4": {
        completed: false,
        currentExercise: "",
        exercises: [],
        progress: {}
      }
    }
  },
  settings: {
    metronomeEnabled: true,
    metronomeVolume: 0.7,
    showHints: true
  }
};

// Clé de base pour le localStorage (préfixée par userId via userStorage)
const BASE_KEY = 'drum-learning-app-progress';

// Fonction pour charger la progression depuis le localStorage
export const loadProgress = () => {
  try {
    const storedData = localStorage.getItem(getStorageKey(BASE_KEY));
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      return parsedData;
    }
    // Si aucune donnée n'existe, retourner la structure initiale
    return initialProgress;
  } catch (error) {
    console.error('Erreur lors du chargement de la progression:', error);
    return initialProgress;
  }
};

// Fonction pour sauvegarder toute la progression dans le localStorage
export const saveProgress = (progressData) => {
  try {
    localStorage.setItem(getStorageKey(BASE_KEY), JSON.stringify(progressData));
    return true;
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de la progression:', error);
    return false;
  }
};

// Trouver le nœud qui contient un exercice spécifique
export const findNodeForExercise = (exerciseId) => {
  const progress = loadProgress();
  const nodes = progress.userProgress.nodes;

  for (const nodeId in nodes) {
    if (nodes[nodeId].exercises.includes(exerciseId)) {
      return nodeId;
    }
  }

  return null;
};

// Fonction pour obtenir le prochain exercice dans un nœud
export const getNextExerciseInNode = (nodeId, currentExerciseId) => {
  const progress = loadProgress();
  const node = progress.userProgress.nodes[nodeId];

  if (!node) {
    return null;
  }

  const currentIndex = node.exercises.indexOf(currentExerciseId);
  
  if (currentIndex === -1 || currentIndex >= node.exercises.length - 1) {
    return null; // Pas d'exercice suivant
  }

  const nextExercise = node.exercises[currentIndex + 1];
  return nextExercise;
};

// Fonction pour mettre à jour la progression d'un exercice spécifique
export const updateExerciseProgress = (exerciseId, newData) => {
  try {
    const currentProgress = loadProgress();
    const nodeId = findNodeForExercise(exerciseId);

    if (!nodeId) {
      // Si l'exercice n'est pas associé à un nœud, vérifier s'il s'agit d'un ID Firebase
      if (exerciseId && exerciseId.length > 15) {
        // Créer une structure temporaire pour cet exercice
        // Vous pouvez adapter cette partie selon vos besoins
        return false;
      }
      console.error(`Aucun nœud trouvé pour l'exercice ${exerciseId}`);
      return false;
    }

    // Initialiser la structure progress si nécessaire
    if (!currentProgress.userProgress.nodes[nodeId].progress) {
      currentProgress.userProgress.nodes[nodeId].progress = {};
    }

    // Initialiser l'objet exercice si nécessaire
    if (!currentProgress.userProgress.nodes[nodeId].progress[exerciseId]) {
      currentProgress.userProgress.nodes[nodeId].progress[exerciseId] = {
        completed: false,
        bestScore: 0,
        attempts: 0,
        lastPlayed: null
      };
    }

    // Mettre à jour les données de l'exercice
    currentProgress.userProgress.nodes[nodeId].progress[exerciseId] = {
      ...currentProgress.userProgress.nodes[nodeId].progress[exerciseId],
      ...newData
    };

    // Si l'exercice est complété et a un score meilleur que le précédent, mettre à jour
    if (newData.bestScore !== undefined) {
      const currentBestScore = currentProgress.userProgress.nodes[nodeId].progress[exerciseId].bestScore;
      currentProgress.userProgress.nodes[nodeId].progress[exerciseId].bestScore =
        Math.max(currentBestScore || 0, newData.bestScore);
    }

    // Mettre à jour le dernier exercice joué
    currentProgress.userProgress.lastPlayedExercise = exerciseId;

    // Mettre à jour l'exercice actuel du nœud
    currentProgress.userProgress.nodes[nodeId].currentExercise = exerciseId;

    // Vérifier si l'exercice complété est le dernier du nœud
    if (newData.completed) {
      const nodeExercises = currentProgress.userProgress.nodes[nodeId].exercises;
      const currentIndex = nodeExercises.indexOf(exerciseId);

      // Si c'est le dernier exercice du nœud
      if (currentIndex === nodeExercises.length - 1) {
        // Marquer le nœud comme complété
        currentProgress.userProgress.nodes[nodeId].completed = true;

        // Déterminer le nœud suivant
        const nodeNumber = parseInt(nodeId.split('-')[1]);
        const nextNodeId = `node-${nodeNumber + 1}`;

        // Si le nœud suivant existe, le mettre comme nœud actuel
        if (currentProgress.userProgress.nodes[nextNodeId]) {
          currentProgress.userProgress.currentHighestNode = nextNodeId;
        }
      } else {
        // S'il y a un exercice suivant, le définir comme exercice actuel du nœud
        currentProgress.userProgress.nodes[nodeId].currentExercise = nodeExercises[currentIndex + 1];
      }
    }

    // Sauvegarder les modifications
    saveProgress(currentProgress);
    return true;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la progression:', error);
    return false;
  }
};

// Fonction pour marquer un exercice comme complété et mettre à jour son score
// alwaysComplete : si true, l'exercice est considéré comme complété dès qu'il est terminé,
// peu importe le score (utilisé pour les exercices standard MIDI importés)
export const completeExercise = (exerciseId, score, { alwaysComplete = false } = {}) => {
  const now = new Date().toISOString();
  const nodeId = findNodeForExercise(exerciseId);

  // Si nous ne trouvons pas de nœud mais que c'est un ID Firebase,
  // on considère qu'il s'agit d'un exercice valide
  if (!nodeId) {
    if (exerciseId && exerciseId.length > 15) {

      // Injecter l'exercice dans le nœud node-1 pour permettre de suivre la progression
      const progress = loadProgress();
      if (!progress.userProgress.nodes["node-1"].exercises.includes(exerciseId)) {
        progress.userProgress.nodes["node-1"].exercises.push(exerciseId);
        saveProgress(progress);
      }

      // Maintenant on peut mettre à jour la progression
      return updateExerciseProgress(exerciseId, {
        completed: alwaysComplete || score >= 85,
        bestScore: score,
        lastPlayed: now,
        attempts: 1
      });
    }

    console.error(`Aucun nœud trouvé pour l'exercice ${exerciseId}`);
    return false;
  }

  const progress = loadProgress();

  // Initialiser l'objet progress si nécessaire
  if (!progress.userProgress.nodes[nodeId].progress) {
    progress.userProgress.nodes[nodeId].progress = {};
  }

  // Initialiser l'objet exercice si nécessaire
  if (!progress.userProgress.nodes[nodeId].progress[exerciseId]) {
    progress.userProgress.nodes[nodeId].progress[exerciseId] = {
      completed: false,
      bestScore: 0,
      attempts: 0,
      lastPlayed: null
    };
  }

  const attempts = progress.userProgress.nodes[nodeId].progress[exerciseId]?.attempts || 0;

  const result = updateExerciseProgress(exerciseId, {
    completed: alwaysComplete || score >= 85, // Considéré comme complété si score >= 85, sauf si alwaysComplete
    bestScore: score,
    lastPlayed: now,
    attempts: attempts + 1
  });

  if (result) {
    emitProgressionChanged('rythmes', { exerciseId, score });
  }

  return result;
};

// Fonction pour vérifier si un exercice est débloqué
export const isExerciseUnlocked = (exerciseId) => {

  const progress = loadProgress();
  const nodeId = findNodeForExercise(exerciseId);


  // Si nous ne trouvons pas de nœud mais qu'il s'agit d'un ID Firebase
  // (les IDs Firebase sont généralement longs avec des caractères alphanumériques)
  if (!nodeId && exerciseId && exerciseId.length > 15) {
    // Débloquer automatiquement les exercices Firebase
    return true;
  }

  if (!nodeId) {
    return false;
  }

  const node = progress.userProgress.nodes[nodeId];

  // Si le nœud n'est pas débloqué, l'exercice n'est pas débloqué
  if (!isNodeUnlocked(nodeId)) {
    return false;
  }

  // Le premier exercice du nœud est toujours débloqué si le nœud est débloqué
  if (node.exercises[0] === exerciseId) {
    return true;
  }

  // Pour les exercices de type 'drill', les débloquer si au moins un exercice du nœud est complété
  if (exerciseId.startsWith('drill-')) {
    // Vérifier si au moins un exercice du nœud est complété
    for (const ex of node.exercises) {
      if (ex !== exerciseId && node.progress[ex]?.completed) {
        return true;
      }
    }
  }

  // Pour les autres exercices, vérifier si l'exercice précédent est complété
  const exerciseIndex = node.exercises.indexOf(exerciseId);
  if (exerciseIndex <= 0) {
    return false;
  }

  const previousExerciseId = node.exercises[exerciseIndex - 1];
  const isUnlocked = node.progress[previousExerciseId]?.completed || false;
  return isUnlocked;
};

// Fonction pour vérifier si un nœud est débloqué
export const isNodeUnlocked = (nodeId) => {
  const progress = loadProgress();

  // Le premier nœud est toujours débloqué
  if (nodeId === "node-1") {
    return true;
  }

  // Pour les autres nœuds, vérifier si le nœud précédent est complété
  const nodeNumber = parseInt(nodeId.split('-')[1]);
  const previousNodeId = `node-${nodeNumber - 1}`;

  const isUnlocked = progress.userProgress.nodes[previousNodeId]?.completed || false;
  return isUnlocked;
};

// Fonction pour obtenir tous les exercices débloqués
export const getUnlockedExercises = () => {
  const progress = loadProgress();
  const unlockedExercises = [];

  // Parcourir tous les nœuds
  Object.keys(progress.userProgress.nodes).forEach(nodeId => {
    const node = progress.userProgress.nodes[nodeId];

    // Si le nœud est débloqué, vérifier ses exercices
    if (isNodeUnlocked(nodeId)) {
      node.exercises.forEach(exerciseId => {
        if (isExerciseUnlocked(exerciseId)) {
          unlockedExercises.push(exerciseId);
        }
      });
    }
  });

  return unlockedExercises;
};

// Fonction pour réinitialiser toute la progression
export const resetAllProgress = () => {
  try {
    localStorage.removeItem(getStorageKey(BASE_KEY));
    return true;
  } catch (error) {
    console.error('Erreur lors de la réinitialisation de la progression:', error);
    return false;
  }
};

/**
 * Injecte automatiquement les exercices MIDI dans la structure de progression
 * @param {Object} progress - La progression actuelle
 * @param {Object} midiConfig - La configuration MIDI contenant les exercices à injecter
 * @returns {Object} La progression mise à jour
 */
export const injectMidiExercisesToProgress = (progress, midiConfig) => {
  // Créer une copie profonde de l'objet progress pour ne pas modifier l'original
  const updatedProgress = JSON.parse(JSON.stringify(progress));

  // Parcourir tous les exercices dans la configuration MIDI
  midiConfig.exercises.forEach(exercise => {
    const { id, nodeId } = exercise;
    

    // Vérifier si le nœud existe, sinon le créer
    if (!updatedProgress.userProgress.nodes[nodeId]) {
      updatedProgress.userProgress.nodes[nodeId] = {
        completed: false,
        currentExercise: id,
        exercises: [],
        progress: {}
      };
    }

    // Ajouter l'exercice à la liste des exercices du nœud s'il n'existe pas encore
    if (!updatedProgress.userProgress.nodes[nodeId].exercises.includes(id)) {
      updatedProgress.userProgress.nodes[nodeId].exercises.push(id);
    } else {
    }

    // Initialiser la progression de l'exercice s'il n'existe pas
    if (!updatedProgress.userProgress.nodes[nodeId].progress) {
      updatedProgress.userProgress.nodes[nodeId].progress = {};
    }
    
    if (!updatedProgress.userProgress.nodes[nodeId].progress[id]) {
      updatedProgress.userProgress.nodes[nodeId].progress[id] = {
        completed: false,
        bestScore: 0,
        attempts: 0,
        lastPlayed: null
      };
    }
  });

  return updatedProgress;
};